import { css, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { useSetAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import React, { useCallback, useEffect, useState } from 'react';

import { Box, Grid, Loader } from '~/components/blocks';
import { ReceptionDrawer } from '~/components/layouts/ReceptionDrawer';
import { ReceptionDrawerFileMenu } from '~/components/layouts/ReceptionDrawer/FileMenu';
import { useActivateLineMessageQuota } from '~/components/pages/Reception/OutpatientQuestionnaireEntryProfileDrawer/use-activate-line-message-quota';
import { FileMenuButton } from '~/components/partials/ReceptionDrawer/FileMenuButton';
import { ReceptionMainBox } from '~/components/partials/ReceptionDrawer/ReceptionMainBox';
import { VisitorProfile } from '~/components/partials/VisitorProfile';
import { ReceptionFileType } from '~/constants/reception_file_type';
import { useSwapDrawer } from '~/hooks/use-swap-drawer';
import { outpatientQuestionnaireEntryProfileDrawerState } from '~/state/reception/atoms';
import { OutpatientQuestionnaireEntryProfileDrawerState as OutpatientQuestionnaireEntryProfileDrawerStateType } from '~/state/reception/types';
import { outpatientQuestionnaireEntryFileAreaState } from '~/state/reception_drawer/atoms';
import { ReceptionDrawerFileType } from '~/state/reception_drawer/types';

import { SendUnavailableModal } from '././SendUnavailableModal';
import { FileArea } from './FileArea';
import { FinishConfirmModal } from './FinishConfirmModal';
import { Footer } from './Footer';
import { NotificationConfirmModal } from './NotificationConfirmModal';
import { OutpatientQuestionnaireEntryKarte } from './OutpatientQuestionnaireEntryKarte';
import { useFetchOutpatientQuestionnaireEntry } from './use-fetch_outpatient_questionnaire_entry';
import { useMarkNotification } from './use-mark-notification';

const ProfileBox = styled(Box)(({ theme }) =>
  css({
    overflow: 'auto',
    padding: `${theme.space.l} ${theme.space.l} ${theme.space.m}`,
    [`@media ${theme.mediaQueries.tablet}`]: {
      padding: `${theme.space.l} ${theme.space.l} ${theme.space.xs}`,
    },
  }),
);

export const OutpatientQuestionnaireEntryProfileDrawer = () => {
  const theme = useTheme();
  const {
    state: { isOpen },
    handleTransitionEnd,
  } = useSwapDrawer<OutpatientQuestionnaireEntryProfileDrawerStateType>(
    outpatientQuestionnaireEntryProfileDrawerState,
    'outpatientQuestionnaireEntryId',
  );
  const [isFileAreaOpened, setOpenFileArea] = useState(false);
  const setFileAreaState = useSetAtom(outpatientQuestionnaireEntryFileAreaState);
  const resetOutpatientQuestionnaireEntryProfileDrawerState = useResetAtom(
    outpatientQuestionnaireEntryProfileDrawerState,
  );

  const { outpatientQuestionnaireEntry, loading } = useFetchOutpatientQuestionnaireEntry();
  useMarkNotification();

  const handleOpenFileArea = useCallback(
    (type: ReceptionDrawerFileType) => {
      setOpenFileArea(true);
      setFileAreaState((state) => ({
        ...state,
        type,
        outpatientQuestionnaireEntryId: outpatientQuestionnaireEntry?.id || null,
      }));
    },
    [outpatientQuestionnaireEntry, setFileAreaState],
  );
  const handleCloseFileArea = useCallback(() => {
    setOpenFileArea(false);
  }, []);
  const handleClose = useCallback(() => {
    resetOutpatientQuestionnaireEntryProfileDrawerState();
  }, [resetOutpatientQuestionnaireEntryProfileDrawerState]);

  useEffect(() => {
    setOpenFileArea(false);
  }, [outpatientQuestionnaireEntry?.id, isOpen]);

  const guestPatient =
    outpatientQuestionnaireEntry?.directVisitor || outpatientQuestionnaireEntry?.lineVisitor;

  const { activateLineMessageQuota, activateLoading, called } = useActivateLineMessageQuota();
  const messageSendable =
    guestPatient?.__typename === 'LineVisitor' ? guestPatient.messageSendable : false;

  useEffect(() => {
    const shouldActivateLineQuota =
      guestPatient?.__typename === 'LineVisitor' && !messageSendable && !activateLoading && !called;

    if (shouldActivateLineQuota) {
      const activateData = async () => {
        await activateLineMessageQuota(guestPatient.id);
      };
      activateData();
    }
  }, [guestPatient, messageSendable, activateLineMessageQuota, activateLoading, called]);

  if (activateLoading) {
    return (
      <ReceptionDrawer
        isOpen={isOpen}
        onClose={handleClose}
        onTransitionEnd={handleTransitionEnd}
        isExpended={isFileAreaOpened}
      >
        <ReceptionMainBox>
          <Loader inside open />
        </ReceptionMainBox>
      </ReceptionDrawer>
    );
  }

  return (
    <>
      <ReceptionDrawer
        isOpen={isOpen}
        onClose={handleClose}
        onTransitionEnd={handleTransitionEnd}
        isExpended={isFileAreaOpened}
      >
        <ReceptionDrawerFileMenu>
          <FileMenuButton
            fileName="処方箋"
            active={false}
            statusLabel="データなし"
            marginTop={theme.space.xl}
          />
          <FileMenuButton fileName="保険情報" active={false} statusLabel="データなし" />
          <FileMenuButton
            fileName="問診票"
            active={true}
            statusLabel="データあり"
            onClick={() => handleOpenFileArea(ReceptionFileType.questionnaire)}
          />
          <FileMenuButton fileName="お薬手帳" active={false} statusLabel="データなし" />
        </ReceptionDrawerFileMenu>
        {isFileAreaOpened && <FileArea onClose={handleCloseFileArea} />}
        <ReceptionMainBox>
          <Grid height="100%" gridTemplateRows="min-content 1fr min-content" overflow="hidden">
            <ProfileBox>
              <VisitorProfile loading={loading} visitor={guestPatient} />
            </ProfileBox>
            {outpatientQuestionnaireEntry ? (
              <Box overflow="auto">
                <OutpatientQuestionnaireEntryKarte
                  outpatientQuestionnaireEntry={outpatientQuestionnaireEntry}
                />
              </Box>
            ) : (
              <Loader inside open />
            )}
            <Footer outpatientQuestionnaireEntry={outpatientQuestionnaireEntry} />
          </Grid>
        </ReceptionMainBox>
      </ReceptionDrawer>
      <NotificationConfirmModal />
      <SendUnavailableModal />
      <FinishConfirmModal />
    </>
  );
};
